import React, { useEffect, useState, useRef } from 'react';
import { Sparkles, Heart, Star, ChevronRight, ArrowDown, Music, Palette, Camera, Sparkle } from 'lucide-react';

const Hero = () => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [scrollProgress, setScrollProgress] = useState(0);
  const [isHovering, setIsHovering] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const containerRef = useRef(null);

  // Check if mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Mouse move effect
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (isMobile) return;
      setMousePosition({
        x: (e.clientX / window.innerWidth) * 40 - 20,
        y: (e.clientY / window.innerHeight) * 40 - 20
      });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, [isMobile]);

  // Scroll progress effect
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset;
      const heroHeight = containerRef.current?.clientHeight || 800;
      const progress = (scrollTop / heroHeight) * 100;
      setScrollProgress(Math.min(progress, 100));
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Animated gradient colors
  const [gradientAngle, setGradientAngle] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => {
      setGradientAngle((prev) => (prev + 1) % 360);
    }, 50);
    return () => clearInterval(interval);
  }, []);

  return (
    <section 
      ref={containerRef}
      id="home" 
      className="min-h-screen relative overflow-hidden bg-gradient-to-br from-pink-50 via-rose-50 to-pink-100"
      onMouseEnter={() => !isMobile && setIsHovering(true)}
      onMouseLeave={() => !isMobile && setIsHovering(false)}
      style={{
        '--gradient-angle': `${gradientAngle}deg`,
        background: !isMobile ? `radial-gradient(circle at ${mousePosition.x + 50}% ${mousePosition.y + 50}%, rgba(255, 182, 193, 0.25) 0%, transparent 60%),
                    radial-gradient(circle at ${100 - mousePosition.x + 50}% ${100 - mousePosition.y + 50}%, rgba(251, 207, 232, 0.3) 0%, transparent 60%),
                    linear-gradient(var(--gradient-angle), rgba(255, 240, 245, 0.9) 0%, rgba(255, 228, 230, 0.8) 50%, rgba(255, 240, 245, 0.9) 100%)` : 
                    `linear-gradient(var(--gradient-angle), rgba(255, 240, 245, 0.9) 0%, rgba(255, 228, 230, 0.8) 50%, rgba(255, 240, 245, 0.9) 100%)`
      }}
    >
      {/* Dynamic background grid */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute inset-0" style={{
          backgroundImage: `linear-gradient(rgba(255, 182, 193, 0.1) 1px, transparent 1px),
                           linear-gradient(90deg, rgba(255, 182, 193, 0.1) 1px, transparent 1px)`,
          backgroundSize: isMobile ? '30px 30px' : '50px 50px',
          maskImage: 'radial-gradient(circle at center, black 30%, transparent 70%)'
        }} />
      </div>

      {/* Enhanced floating particles with trails */}
      <div className="absolute inset-0 overflow-hidden">
        {[...Array(isMobile ? 15 : 30)].map((_, i) => (
          <div
            key={i}
            className="absolute rounded-full animate-float"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              width: `${Math.random() * (isMobile ? 3 : 5) + 1}px`,
              height: `${Math.random() * (isMobile ? 3 : 5) + 1}px`,
              background: `radial-gradient(circle, 
                rgba(${Math.random() * 55 + 200}, ${Math.random() * 100 + 155}, ${Math.random() * 100 + 155}, 0.8) 0%,
                rgba(${Math.random() * 55 + 200}, ${Math.random() * 100 + 155}, ${Math.random() * 100 + 155}, 0) 70%
              )`,
              animationDelay: `${i * 0.1}s`,
              animationDuration: `${Math.random() * 10 + 5}s`,
              filter: 'blur(1px)',
              transform: `rotate(${Math.random() * 360}deg)`
            }}
          />
        ))}
      </div>

      {/* Parallax floating elements with enhanced effects - Adjust sizes for mobile */}
      {!isMobile && (
        <>
          <div 
            className="absolute top-20 left-10 w-32 md:w-48 lg:w-64 h-32 md:h-48 lg:h-64 bg-gradient-to-br from-pink-400/20 via-rose-400/20 to-pink-400/10 rounded-full mix-blend-multiply filter blur-3xl md:blur-4xl opacity-50 animate-float-slow"
            style={{
              transform: `translate(${mousePosition.x * 0.5}px, ${mousePosition.y * 0.5}px) rotate(${mousePosition.x * 0.5}deg)`,
              animationDelay: '0.5s'
            }}
          />
          
          <div 
            className="absolute bottom-32 right-10 w-40 md:w-56 lg:w-72 h-40 md:h-56 lg:h-72 bg-gradient-to-tr from-rose-300/30 via-pink-300/30 to-rose-300/20 rounded-full mix-blend-multiply filter blur-3xl md:blur-4xl opacity-40 animate-float"
            style={{
              transform: `translate(${-mousePosition.x * 0.3}px, ${-mousePosition.y * 0.3}px) rotate(${-mousePosition.x * 0.3}deg)`,
              animationDelay: '1s'
            }}
          />
          
          <div 
            className="absolute top-1/3 left-1/4 w-24 md:w-32 lg:w-48 h-24 md:h-32 lg:h-48 bg-gradient-to-br from-pink-200/40 via-rose-200/40 to-pink-200/30 rounded-full mix-blend-multiply filter blur-2xl md:blur-3xl opacity-40 animate-float-slower"
            style={{
              transform: `translate(${mousePosition.x * 0.8}px, ${mousePosition.y * 0.8}px) rotate(${mousePosition.y * 0.5}deg)`,
              animationDelay: '1.5s'
            }}
          />
        </>
      )}

      {/* Animated decorative icons with hover effects - Adjust positions for mobile */}
      <div className="absolute top-10 right-4 sm:right-10 md:right-20 group cursor-pointer">
        <div className="absolute inset-0 bg-pink-400/10 rounded-full blur-lg md:blur-xl group-hover:blur-xl md:group-hover:blur-2xl transition-all duration-500"></div>
        <Sparkles 
          className="relative text-pink-500/70 w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 animate-spin-slow group-hover:scale-110 md:group-hover:scale-125 group-hover:text-pink-600 transition-all duration-500"
          style={!isMobile ? { transform: `translate(${mousePosition.x * 0.2}px, ${mousePosition.y * 0.2}px) rotate(${mousePosition.x * 0.5}deg)` } : {}}
        />
      </div>
      
      <div className="absolute bottom-20 sm:bottom-32 left-4 sm:left-10 md:left-20 group cursor-pointer">
        <div className="absolute inset-0 bg-rose-400/10 rounded-full blur-lg md:blur-xl group-hover:blur-xl md:group-hover:blur-2xl transition-all duration-500"></div>
        <Heart 
          className="relative text-rose-500/80 w-7 h-7 sm:w-9 sm:h-9 md:w-10 md:h-10 animate-pulse group-hover:scale-110 md:group-hover:scale-125 group-hover:text-rose-600 group-hover:fill-rose-400/30 transition-all duration-500"
          style={!isMobile ? { transform: `translate(${mousePosition.x * 0.3}px, ${mousePosition.y * 0.3}px)` } : {}}
        />
      </div>
      
      <div className="absolute top-32 left-1/4 md:left-1/3 group cursor-pointer hidden sm:block">
        <div className="absolute inset-0 bg-pink-500/10 rounded-full blur-lg md:blur-xl group-hover:blur-xl md:group-hover:blur-2xl transition-all duration-500"></div>
        <Star 
          className="relative text-pink-600/60 w-8 h-8 sm:w-10 sm:h-10 md:w-11 md:h-11 animate-spin group-hover:scale-110 md:group-hover:scale-125 group-hover:text-pink-700 transition-all duration-500"
          style={!isMobile ? { transform: `translate(${mousePosition.x * 0.25}px, ${mousePosition.y * 0.25}px) rotate(${mousePosition.y * 0.5}deg)` } : {}}
        />
      </div>

      {/* Interactive floating elements - Hide on small mobile */}
      {!isMobile && (
        <>
          <div className="absolute top-40 left-4 sm:left-10 md:left-20 hidden sm:block">
            <div className="relative group">
              <div className="absolute -inset-3 sm:-inset-4 bg-gradient-to-r from-pink-400/20 to-rose-400/20 rounded-full blur-md sm:blur-lg group-hover:blur-lg sm:group-hover:blur-xl transition-all duration-500"></div>
              <div className="relative w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 bg-gradient-to-br from-white/90 to-pink-50/90 backdrop-blur-sm rounded-2xl flex items-center justify-center shadow-xl animate-float group-hover:shadow-2xl transition-all duration-500">
                <Palette className="w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8 text-pink-600/80 group-hover:scale-110 transition-transform duration-500" />
              </div>
            </div>
          </div>

          <div className="absolute bottom-40 right-4 sm:right-10 md:right-20 hidden sm:block">
            <div className="relative group">
              <div className="absolute -inset-3 sm:-inset-4 bg-gradient-to-r from-rose-400/20 to-pink-400/20 rounded-full blur-md sm:blur-lg group-hover:blur-lg sm:group-hover:blur-xl transition-all duration-500"></div>
              <div className="relative w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 bg-gradient-to-br from-white/90 to-rose-50/90 backdrop-blur-sm rounded-2xl flex items-center justify-center shadow-xl animate-float-slow group-hover:shadow-2xl transition-all duration-500">
                <Camera className="w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8 text-rose-600/80 group-hover:scale-110 transition-transform duration-500" />
              </div>
            </div>
          </div>
        </>
      )}

      {/* Scroll progress indicator */}
      <div className="fixed top-0 left-0 right-0 h-1 z-50">
        <div 
          className="h-full bg-gradient-to-r from-pink-500 via-rose-500 to-pink-500 transition-all duration-300"
          style={{ width: `${scrollProgress}%` }}
        />
      </div>

      {/* Main content with enhanced animations */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 h-screen flex items-center justify-center relative z-10 lg:mt-25">
        <div className="max-w-6xl mx-auto text-center relative px-4">
          {/* Main name with enhanced effects - Responsive text sizes with larger mobile text */}
          <div className="mb-4 sm:mb-6 md:mb-8 relative">
            <div className="absolute -inset-4 sm:-inset-6 md:-inset-8 bg-gradient-to-r from-pink-400/10 via-rose-400/10 to-pink-400/10 blur-xl sm:blur-2xl md:blur-3xl rounded-full opacity-50"></div>
            
           <div className="overflow-hidden mb-1 sm:mb-2 relative text-center">
  <h1
    className="
      inline-block
      text-5xl sm:text-6xl md:text-7xl lg:text-8xl xl:text-9xl
      font-extrabold
      tracking-tight
      bg-gradient-to-r from-pink-400 via-pink-500 to-fuchsia-500
      bg-clip-text  
       text-pink-600
      font-fathima
      animate-slide-up-stagger
      relative
      opacity-100
      [text-shadow:_0_0_30px_rgba(236,72,153,0.5)]
    "
    style={{ animationDelay: '200ms' }}
  >
    <span className="relative inline-block">
      Fathima

      {/* Neon sparkle */}
      <Sparkle
        className="
          absolute -top-2 -right-2 sm:-top-3 sm:-right-3 md:-top-4 md:-right-4
          w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8
          text-pink-300
          animate-spin-slow
          drop-shadow-[0_0_15px_#ec4899]
          filter brightness-125
        "
      />

      {/* Neon underline */}
      <span
        className="
          absolute left-0 -bottom-3
          w-full h-[2px]
          bg-gradient-to-r from-transparent via-pink-400 to-transparent
          animate-width-pulse
          shadow-[0_0_10px_#ec4899]
        "
      />

      {/* Strong pink glow behind text */}
      <span
        className="
          absolute -inset-6
          blur-3xl opacity-50
          bg-gradient-to-r from-pink-500/40 to-fuchsia-500/40
          -z-10
          animate-pulse
        "
      />
    </span>
  </h1>
</div>




            
            <div className="overflow-hidden relative text-center">
  <h2
    className="
      text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl 2xl:text-9xl
      font-extrabold
      text-pink-600
      tracking-tight
      relative
      animate-slide-up-stagger
    "
    style={{ animationDelay: '400ms' }}
  >
    <span className="relative inline-block">
      Zahara

      {/* Sparkle accent */}
      <Sparkle
        className="
          absolute -top-2 -right-2 sm:-top-3 sm:-right-3 md:-top-4 md:-right-4
          w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8
          text-pink-400
          animate-spin-slow
        "
      />

      {/* Optional subtle underline */}
      <span
        className="
          absolute left-0 -bottom-1 w-full h-[2px]
          bg-gradient-to-r from-transparent via-pink-400 to-transparent
          animate-width-pulse
        "
      />
    </span>
  </h2>
</div>

          </div>
          
          {/* Tagline with enhanced effects */}
          <div 
            className="mb-6 sm:mb-8 md:mb-10 opacity-0 animate-fade-in-stagger"
            style={{ animationDelay: '600ms' }}
          >
            <div className="relative inline-block group">
              <div className="absolute -inset-2 sm:-inset-3 md:-inset-4 bg-gradient-to-r from-pink-400 via-rose-400 to-pink-400 animate-gradient-x blur-xl sm:blur-2xl opacity-30 group-hover:opacity-50 transition-opacity duration-500 rounded-full"></div>
              <div className="relative">
                <div className="absolute -inset-1 sm:-inset-1 bg-gradient-to-r from-pink-400 to-rose-400 blur opacity-30 rounded-full animate-pulse-slow"></div>
                <p className="relative text-base sm:text-lg md:text-xl lg:text-2xl xl:text-3xl font-light text-gray-700 tracking-wider px-4 sm:px-6 md:px-8 lg:px-10 py-2 sm:py-3 md:py-4 bg-white/80 backdrop-blur-sm rounded-full shadow-lg">
                  <span className="inline-flex items-center gap-2 sm:gap-3 md:gap-4">
                    <Music className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-pink-500 animate-pulse" />
                    Grace • Dreams • Elegance
                    <Music className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-rose-500 animate-pulse" />
                  </span>
                </p>
              </div>
            </div>
          </div>
          
          {/* Subtitle with fade-in effect */}
          <div 
            className="mb-8 sm:mb-10 md:mb-12 opacity-0 animate-fade-in-stagger"
            style={{ animationDelay: '800ms' }}
          >
            <div className="relative px-2 sm:px-0">
              <div className="absolute -inset-3 sm:-inset-4 bg-gradient-to-r from-pink-100/50 to-rose-100/50 blur-lg sm:blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              <p className="relative text-sm sm:text-base md:text-lg lg:text-xl xl:text-2xl text-gray-600 max-w-xs sm:max-w-sm md:max-w-md lg:max-w-2xl xl:max-w-3xl mx-auto leading-relaxed sm:leading-relaxed font-medium animate-fade-in-stagger" style={{ animationDelay: '1000ms' }}>
                A journey of elegance and dreams, woven with grace. Creating beauty in every moment and inspiration in every step.
              </p>
            </div>
          </div>
          
          {/* Enhanced CTA Buttons - Stack on mobile */}
          <div 
            className="flex flex-col sm:flex-row gap-4 sm:gap-6 md:gap-8 justify-center opacity-0 animate-fade-in-stagger"
            style={{ animationDelay: '1000ms' }}
          >
            {/* Primary Button */}
            <button className="group relative px-6 py-3 sm:px-8 sm:py-4 md:px-10 md:py-5 overflow-hidden rounded-2xl sm:rounded-3xl font-medium transition-all duration-700 hover:scale-105 hover:shadow-2xl">
              <div className="absolute inset-0 bg-gradient-to-r from-rose-500 via-pink-500 to-rose-500 animate-gradient-x"></div>
              <div className="absolute inset-0 bg-gradient-to-r from-rose-600 via-pink-600 to-rose-600 opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>
              <div className="absolute top-0 left-0 right-0 h-1 bg-white/50 transform -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
              <span className="relative flex items-center justify-center gap-2 sm:gap-3 md:gap-4 text-white text-sm sm:text-base md:text-lg">
                <span> <a href="#destinations"> <button>Explore My Journey</button></a> </span>
                <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 group-hover:translate-x-1 sm:group-hover:translate-x-2 transition-transform duration-500" />
              </span>
              <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/40 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-700 origin-left"></div>
            </button>
            
            {/* Secondary Button */}
            <button className="group relative px-6 py-3 sm:px-8 sm:py-4 md:px-10 md:py-5 overflow-hidden rounded-2xl sm:rounded-3xl font-medium bg-white/90 backdrop-blur-sm transition-all duration-700 hover:scale-105 hover:shadow-2xl">
              <div className="absolute inset-0 bg-gradient-to-r from-pink-100 to-rose-100 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              <div className="absolute inset-0 border-2 border-transparent group-hover:border-pink-400/30 rounded-2xl sm:rounded-3xl transition-all duration-500"></div>
              <span className="relative flex items-center justify-center gap-2 sm:gap-3 md:gap-4 text-gray-700 text-sm sm:text-base md:text-lg">
                <span> <a href="#contact"> <button> Connect With Me</button> </a> </span>
                <Heart className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 group-hover:fill-pink-400 group-hover:text-pink-500 group-hover:scale-110 sm:group-hover:scale-125 transition-all duration-500" />
              </span>
              <div className="absolute inset-0 bg-gradient-to-r from-pink-400/0 via-pink-400/10 to-pink-400/0 transform -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
            </button>
          </div>
        </div>
      </div>

      {/* Enhanced scroll indicator */}
      <div className="absolute bottom-4 sm:bottom-8 md:bottom-12 left-1/2 transform -translate-x-1/2 z-20">
        <div className="flex flex-col items-center group cursor-pointer animate-bounce-slow">
          <div className="text-xs sm:text-sm text-pink-600/70 mb-2 sm:mb-3 md:mb-4 tracking-wider font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-500 hidden sm:block">
            Scroll to discover more
          </div>
          <div className="relative">
            <div className="absolute -inset-2 sm:-inset-3 md:-inset-4 bg-gradient-to-b from-pink-400/20 to-transparent rounded-full blur-md sm:blur-lg opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            <div className="relative w-8 h-14 sm:w-10 sm:h-16 md:w-12 md:h-20 border-2 border-pink-400/40 rounded-xl sm:rounded-2xl flex justify-center overflow-hidden backdrop-blur-sm bg-white/20">
              <div className="w-1.5 h-4 sm:w-1.5 sm:h-5 md:w-2 md:h-6 bg-gradient-to-b from-pink-500 to-rose-500 rounded-full mt-2 sm:mt-3 md:mt-3 animate-scroll-pulse"></div>
            </div>
            <ArrowDown className="absolute -top-4 sm:-top-5 md:-top-6 left-1/2 transform -translate-x-1/2 w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-pink-500/60 group-hover:text-pink-600 group-hover:scale-110 sm:group-hover:scale-125 transition-all duration-500" />
          </div>
        </div>
      </div>

      {/* Glitch effect overlay (optional) - Only on desktop */}
      {!isMobile && isHovering && (
        <div className="absolute inset-0 pointer-events-none mix-blend-overlay opacity-5">
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-pink-400/10 to-transparent animate-glitch"></div>
        </div>
      )}
    </section>
  );
};

// Enhanced CSS Animations with responsive adjustments
const styles = `
@keyframes float {
  0%, 100% { 
    transform: translateY(0) rotate(0deg) scale(1); 
  }
  33% { 
    transform: translateY(-20px) rotate(5deg) scale(1.05); 
  }
  66% { 
    transform: translateY(-10px) rotate(-3deg) scale(0.95); 
  }
}

@keyframes float-slow {
  0%, 100% { 
    transform: translateY(0) rotate(0deg); 
  }
  50% { 
    transform: translateY(-15px) rotate(-2deg); 
  }
}

@keyframes float-slower {
  0%, 100% { 
    transform: translateY(0) rotate(0deg); 
  }
  50% { 
    transform: translateY(-8px) rotate(1deg); 
  }
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

@keyframes spin-slow {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

@keyframes scroll-pulse {
  0%, 100% { 
    transform: translateY(0); 
    opacity: 1;
  }
  50% { 
    transform: translateY(20px); 
    opacity: 0.3;
  }
}

@keyframes slide-up-stagger {
  from {
    opacity: 0;
    transform: translateY(30px) scale(0.9);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes fade-in-stagger {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes bounce-slow {
  0%, 100% { 
    transform: translateY(0); 
  }
  50% { 
    transform: translateY(-10px); 
  }
}

@keyframes gradient-x {
  0%, 100% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
}

@keyframes gradient-flow {
  0%, 100% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
}

@keyframes width-pulse {
  0%, 100% { width: 0; left: 50%; }
  50% { width: 100%; left: 0; }
}

@keyframes pulse-slow {
  0%, 100% { opacity: 0.3; }
  50% { opacity: 0.6; }
}

@keyframes glitch {
  0%, 100% { transform: translateX(0); }
  20% { transform: translateX(-2px); }
  40% { transform: translateX(2px); }
  60% { transform: translateX(-1px); }
  80% { transform: translateX(1px); }
}

.animate-float {
  animation: float 6s ease-in-out infinite;
}

.animate-float-slow {
  animation: float-slow 10s ease-in-out infinite;
}

.animate-float-slower {
  animation: float-slower 15s ease-in-out infinite;
}

.animate-spin {
  animation: spin 25s linear infinite;
}

.animate-spin-slow {
  animation: spin-slow 40s linear infinite;
}

.animate-scroll-pulse {
  animation: scroll-pulse 2s ease-in-out infinite;
}

.animate-slide-up-stagger {
  animation: slide-up-stagger 1s cubic-bezier(0.4, 0, 0.2, 1) forwards;
}

.animate-fade-in-stagger {
  animation: fade-in-stagger 1s cubic-bezier(0.4, 0, 0.2, 1) forwards;
}

.animate-bounce-slow {
  animation: bounce-slow 3s ease-in-out infinite;
}

.animate-gradient-x {
  animation: gradient-x 3s ease infinite;
  background-size: 200% 200%;
}

.animate-gradient-flow {
  animation: gradient-flow 3s ease infinite;
  background-size: 200% auto;
}

.animate-width-pulse {
  animation: width-pulse 3s ease-in-out infinite;
}

.animate-pulse-slow {
  animation: pulse-slow 4s ease-in-out infinite;
}

.animate-glitch {
  animation: glitch 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94) infinite;
}

/* Smooth transitions */
* {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Reduced motion preference */
@media (prefers-reduced-motion: reduce) {
  .animate-float,
  .animate-float-slow,
  .animate-float-slower,
  .animate-spin,
  .animate-spin-slow,
  .animate-scroll-pulse,
  .animate-slide-up-stagger,
  .animate-fade-in-stagger,
  .animate-bounce-slow,
  .animate-gradient-x,
  .animate-gradient-flow,
  .animate-width-pulse,
  .animate-pulse-slow,
  .animate-glitch {
    animation: none;
  }
}

/* Responsive adjustments for mobile text */
@media (max-width: 768px) {
  .animate-slide-up-stagger {
    animation: slide-up-stagger 0.8s cubic-bezier(0.4, 0, 0.2, 1) forwards;
  }
  
  .animate-fade-in-stagger {
    animation: fade-in-stagger 0.8s cubic-bezier(0.4, 0, 0.2, 1) forwards;
  }
}

/* Ensure consistent pink/rose color scheme */
.text-pink-500, .text-rose-500,
.bg-pink-500, .bg-rose-500,
.from-pink-500, .to-pink-500,
.from-rose-500, .to-rose-500,
.border-pink-400, .border-rose-400 {
  /* These are the original pink/rose colors - keeping them consistent */
}
`;

// Add styles to document
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement("style");
  styleSheet.innerText = styles;
  document.head.appendChild(styleSheet);
}

export default Hero;